import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { useSesion } from '../auth/useSesion'

/**
 * Pantalla de acceso, en dos mitades de altura completa.
 *
 * A la izquierda, el panel de marca, con tres anclajes verticales y mucho aire
 * entre ellos: isotipo arriba, la promesa del producto en el tercio medio-bajo
 * y el pie abajo. De fondo, un patron de curvas de nivel a muy baja opacidad
 * que le quita la planitud sin competir con el texto.
 *
 * A la derecha, el formulario y nada mas: columna estrecha centrada, sin
 * tarjeta ni sombras. La profundidad sale de escalones de luminosidad entre
 * fondo, campos y bordes.
 *
 * Es un `<form>` de verdad, no un boton suelto: asi se envia con Enter desde
 * cualquiera de los dos campos, que es como la gente rellena un formulario de
 * dos lineas.
 *
 * **En movil el panel de marca desaparece.** Ocupa media pantalla para no decir
 * nada que el usuario necesite en ese momento; lo que queda es el formulario
 * con el rotulo de la casa encima.
 *
 * La entrada esta escalonada con un retardo por elemento. Es lo unico animado
 * de la pantalla y se apaga entero con `prefers-reduced-motion`.
 */

/** Retardo de entrada. Un indice por elemento, en el orden en que se leen. */
function entrada(indice: number): React.CSSProperties {
  return { animationDelay: `${80 + indice * 70}ms` }
}

// `bg-lienzo` y no `bg-superficie`: el formulario ya va sobre `superficie`, asi
// que un campo del mismo color desapareceria. El lienzo es la superficie mas
// hundida de las tres, y un campo de texto es justo eso, un hueco.
const CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-lienzo px-4 py-2.5 text-sm text-tinta outline-none transition-colors placeholder:text-tinta-apagada focus:border-foco focus:ring-2 focus:ring-foco-halo'

const ETIQUETA = 'text-sm font-medium text-tinta-2'

// No hay endpoint de recuperacion: las contrasenas las rotan los
// administradores a mano. Asi que el enlace no lleva a una pantalla que no
// existe, sino que explica a quien hay que pedirselo. Si esta puesto el correo
// de soporte, ademas abre el cliente de correo con el asunto escrito.
const CORREO_SOPORTE: string | undefined = import.meta.env.VITE_CORREO_SOPORTE

/** Curvas de nivel del panel. Decorativo: no lleva texto ni recibe eventos. */
function CurvasDeNivel() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 size-full text-marca-acento/20"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 600 800"
      fill="none"
    >
      <g stroke="currentColor" strokeWidth="1">
        {/* La cima, arriba a la derecha: ovalos concentricos que se abren. */}
        {[0, 1, 2, 3, 4].map((i) => (
          <ellipse key={i} cx="470" cy="70" rx={40 + i * 46} ry={24 + i * 28} />
        ))}
        {/* El derrame: ondas largas que barren la mitad inferior. */}
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <path
            key={i}
            d={`M-40 ${330 + i * 74} C 130 ${286 + i * 74}, 300 ${392 + i * 74}, 640 ${300 + i * 74}`}
          />
        ))}
      </g>
    </svg>
  )
}

export function Login() {
  const { sesion, cargando, entrar } = useSesion()
  const [correo, setCorreo] = useState('')
  const [contrasena, setContrasena] = useState('')
  const [verContrasena, setVerContrasena] = useState(false)
  const [verAyuda, setVerAyuda] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Ya identificado: no tiene sentido enseñar el formulario.
  if (!cargando && sesion) return <Navigate to="/logistica" replace />

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      await entrar(correo, contrasena)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se ha podido iniciar sesion')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-superficie lg:grid-cols-2">
      {/* ── Panel de marca ─────────────────────────────────────────────── */}
      {/* El formulario va sobre `superficie` y el panel sobre `marca`, que es
          mas honda: en oscuro lo que se eleva se aclara, asi que la mitad del
          formulario queda por encima y la division se lee sola. */}
      <aside className="relative hidden overflow-hidden bg-marca lg:flex lg:flex-col lg:justify-between lg:px-14 lg:py-16">
        <CurvasDeNivel />

        {/* Isotipo + rotulo, arriba del todo. */}
        <div
          className="relative flex animate-[surgir_.5s_ease-out_both] items-center gap-3.5 motion-reduce:animate-none"
          style={entrada(0)}
        >
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-xl bg-marca-tinta text-lg font-bold tracking-tight text-marca"
          >
            T
          </span>
          <span className="max-w-[22ch] text-xl font-bold leading-tight tracking-tight text-marca-tinta">
            Panel de Operaciones - Tactica Empresarial
          </span>
        </div>

        <div className="relative flex flex-col gap-6">
          <h2
            className="max-w-[16ch] animate-[surgir_.5s_ease-out_both] text-balance text-[2.75rem] font-bold leading-[1.08] tracking-tight text-marca-tinta motion-reduce:animate-none"
            style={entrada(1)}
          >
            Toda la operacion de tu equipo, en un solo lugar.
          </h2>

          <p
            className="max-w-[46ch] animate-[surgir_.5s_ease-out_both] text-base leading-relaxed text-marca-tinta-2 motion-reduce:animate-none"
            style={entrada(2)}
          >
            Logistica, conversaciones, avisos y agendamiento sincronizados en tiempo real, con
            acceso seguro desde cualquier dispositivo.
          </p>
        </div>

        {/* La autoria, al pie: dice quien lo hace sin competir con el rotulo. */}
        <p
          className="relative animate-[surgir_.5s_ease-out_both] text-[13px] text-marca-tinta-2 motion-reduce:animate-none"
          style={entrada(3)}
        >
          Desarrollado por Servicios Digitales
        </p>
      </aside>

      {/* ── Formulario ─────────────────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-[400px]">
          {/* Solo en movil, donde el panel de marca no esta: sin esto la
              pantalla empezaria directamente en "Inicia sesion". */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-xl bg-acento text-base font-bold tracking-tight text-tinta-inversa"
            >
              T
            </span>
            <span className="text-base font-bold leading-tight tracking-tight text-tinta">
              Panel de Operaciones - Tactica Empresarial
            </span>
          </div>

          <h1 className="text-[1.75rem] font-bold tracking-tight text-tinta">Inicia sesion</h1>
          <p className="mt-3.5 text-[15px] text-tinta-apagada">
            Bienvenido de nuevo. Introduce tus datos para continuar.
          </p>

          <form onSubmit={alEnviar} className="mt-10 flex flex-col gap-6">
            <div className="flex flex-col gap-2.5">
              <label htmlFor="correo" className={ETIQUETA}>
                Correo electronico
              </label>
              <input
                id="correo"
                name="correo"
                type="email"
                autoComplete="username"
                required
                autoFocus
                placeholder="nombre@aguasdecastellon.es"
                value={correo}
                onChange={(evento) => setCorreo(evento.target.value)}
                className={CAMPO}
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor="contrasena" className={ETIQUETA}>
                  Contrasena
                </label>
                <button
                  type="button"
                  onClick={() => setVerAyuda((abierta) => !abierta)}
                  aria-expanded={verAyuda}
                  aria-controls="ayuda-contrasena"
                  className="rounded text-[13px] font-medium text-acento-tinta transition-colors hover:text-acento-fuerte hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco-halo"
                >
                  He olvidado mi contrasena
                </button>
              </div>
              {/* El ojo va dentro del campo, asi que el hueco de la derecha se
                  reserva con padding y el boton se posiciona encima. */}
              <div className="relative">
                <input
                  id="contrasena"
                  name="contrasena"
                  type={verContrasena ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="Tu contrasena"
                  value={contrasena}
                  onChange={(evento) => setContrasena(evento.target.value)}
                  className={`${CAMPO} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setVerContrasena((visible) => !visible)}
                  aria-label={verContrasena ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                  aria-pressed={verContrasena}
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-tinta-apagada transition-colors hover:text-tinta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco-halo"
                >
                  {verContrasena ? (
                    <svg
                      aria-hidden="true"
                      className="size-[18px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c7 0 10 7 10 7a18 18 0 0 1-2.4 3.5M6.6 6.6A18 18 0 0 0 2 12s3 7 10 7a10.8 10.8 0 0 0 5.4-1.4" />
                      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M2 2l20 20" />
                    </svg>
                  ) : (
                    <svg
                      aria-hidden="true"
                      className="size-[18px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              {verAyuda ? (
                <p
                  id="ayuda-contrasena"
                  className="rounded-lg border border-acento-borde bg-acento-suave px-3.5 py-3 text-[13px] leading-relaxed text-tinta-2"
                >
                  El panel no envia correos de recuperacion: las contrasenas las restablece un
                  administrador.{' '}
                  {CORREO_SOPORTE ? (
                    <a
                      href={`mailto:${CORREO_SOPORTE}?subject=${encodeURIComponent('Restablecer contrasena del panel')}`}
                      className="font-medium text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
                    >
                      Escribe a {CORREO_SOPORTE}
                    </a>
                  ) : (
                    <span>Pideselo al administrador del panel</span>
                  )}{' '}
                  indicando tu correo y te daran una nueva.
                </p>
              ) : null}
            </div>

            {error ? (
              <p
                role="alert"
                className="rounded-lg border border-negativo-anillo bg-negativo-fondo px-3 py-2 text-sm text-negativo-tinta"
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={enviando}
              className="mt-1 w-full rounded-lg bg-acento px-3 py-3 text-[15px] font-semibold text-tinta-inversa transition-colors hover:bg-acento-fuerte disabled:cursor-not-allowed disabled:bg-desactivado disabled:text-desactivado-tinta"
            >
              {enviando ? 'Entrando...' : 'Iniciar sesion'}
            </button>
          </form>

          {/* La recuperacion vive junto al campo de contrasena, que es donde se
              busca cuando hace falta. Aqui abajo solo queda el sello. */}
          <div className="mt-11 flex flex-col items-center gap-3 text-center">
            <p className="flex items-center gap-2 text-[13px] text-tinta-apagada">
              <svg
                aria-hidden="true"
                className="size-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Conexion cifrada
            </p>
            <p className="text-[13px] text-tinta-apagada">
              El acceso lo da el administrador del panel.
            </p>
            {/* En movil el panel de marca no esta, asi que la autoria se ve
                aqui. En escritorio ya sale al pie del panel: no se repite. */}
            <p className="text-[13px] text-tinta-apagada lg:hidden">
              Desarrollado por Servicios Digitales
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
